<template>
   <div class="py-10 px-4 sm:px-6 lg:px-24">
      <main class="flex flex-col gap-8">
         <div>
            <h1 class="text-xl font-bold text-gray-900">Loyalty Program</h1>
            <p class="text-xs font-semibold text-gray-500 mt-1">Track your agency rewards points and ledger logs</p>
         </div>

         <!-- Loyalty Reward Target Card (Agency Admin Only) -->
         <div v-if="isAgencyAdmin && loyaltySummary" 
              class="bg-gradient-to-r from-emerald-800 to-teal-950 rounded-3xl p-6 text-white shadow-lg border border-teal-700/30 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
            <div class="absolute -right-16 -top-16 w-48 h-48 bg-teal-500/10 rounded-full blur-3xl pointer-events-none"></div>
            <div class="absolute -left-16 -bottom-16 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>
            
            <div class="flex-grow w-full md:w-auto">
               <div class="flex items-center gap-2 mb-1">
                  <font-awesome-icon icon="fa-solid fa-gift" class="text-xl text-emerald-400" />
                  <h3 class="text-lg font-extrabold tracking-wide uppercase">Loyalty Program Reward</h3>
               </div>
               <p class="text-xs text-emerald-300 font-semibold mb-3">
                  * Loyalty Program will be calculated from 01-07-2026
               </p>
               <p class="text-sm text-teal-100 font-semibold leading-relaxed max-w-2xl">
                  You have accumulated <span class="text-white font-extrabold text-lg px-1.5 py-0.5 rounded bg-white/10">{{ loyaltySummary.points_earned }}</span> points 
                  (<span class="text-emerald-300 font-bold">${{ (Number(loyaltySummary.points_earned) * (Number(loyaltySummary.redeem_rate) || 0.1)).toFixed(2) }} USD</span>). 
                  Reach the target of <span class="font-extrabold">{{ loyaltySummary.total_points }}</span> points 
                  (<span class="text-teal-300 font-bold">${{ (Number(loyaltySummary.total_points) * (Number(loyaltySummary.redeem_rate) || 0.1)).toFixed(2) }} USD</span>) to request a withdrawal!
               </p>
               <div class="mt-5 w-full bg-teal-950/50 rounded-full h-3 border border-teal-800/30">
                  <div class="bg-gradient-to-r from-emerald-400 to-teal-300 h-full rounded-full transition-all duration-500 shadow-[0_0_8px_rgba(52,211,153,0.5)]" 
                       :style="{ width: `${Math.min(100, ((Number(loyaltySummary.points_earned) || 0) / Math.max(1, Number(loyaltySummary.total_points) || 1)) * 100)}%` }"></div>
               </div>
               <div class="flex justify-between items-center mt-2 text-xs font-semibold text-teal-300">
                  <span>0 Points</span>
                  <span>{{ Math.min(100, Math.round(((Number(loyaltySummary.points_earned) || 0) / Math.max(1, Number(loyaltySummary.total_points) || 1)) * 100)) }}% Completed</span>
                  <span>{{ loyaltySummary.total_points }} Points Target</span>
               </div>
            </div>
            
            <div class="flex-shrink-0 w-full md:w-auto text-right">
               <!-- 1. Processing request -->
               <button v-if="loyaltySummary.pending_withdraw" 
                       disabled
                       class="w-full md:w-auto px-6 py-3.5 bg-emerald-900/50 border border-emerald-700/50 text-emerald-300/80 font-extrabold text-sm rounded-xl cursor-not-allowed uppercase tracking-wider">
                  Withdrawal Processing
               </button>
                <!-- 2. Reached threshold and can withdraw -->
                <button v-else-if="Number(loyaltySummary.points_earned) >= Number(loyaltySummary.total_points)" 
                        @click="handleRedeemPoints" 
                        :disabled="isProfileLoading"
                        class="w-full md:w-auto px-6 py-3.5 bg-white text-emerald-900 hover:bg-emerald-50 active:scale-[0.98] transition-all duration-200 font-extrabold text-sm rounded-xl shadow-md uppercase tracking-wider min-w-[160px] text-center flex items-center justify-center">
                   <span v-if="isProfileLoading" class="flex items-center gap-2">
                      <svg class="animate-spin h-4 w-4 text-emerald-900" fill="none" viewBox="0 0 24 24">
                         <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                         <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading...
                   </span>
                   <span v-else>Withdraw Now</span>
                </button>
               <!-- 3. Under threshold - Faded / Disabled -->
               <button v-else
                       disabled
                       class="w-full md:w-auto px-6 py-3.5 bg-white/20 text-white/50 border border-white/10 font-extrabold text-sm rounded-xl cursor-not-allowed uppercase tracking-wider">
                  Withdraw Now
               </button>
            </div>
         </div>

         <!-- Points Ledger Table -->
         <div class="flow-root mt-4">
            <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
               <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                  <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                     <table class="min-w-full divide-y divide-gray-300">
                        <thead class="bg-gray-50">
                           <tr>
                              <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Date</th>
                              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Booking ID</th>
                              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900" v-if="isAgencyAdmin">Earned By</th>
                              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Booking Amount</th>
                              <th scope="col" class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900 pr-6">Points</th>
                           </tr>
                        </thead>
                        <tbody class="divide-y divide-gray-200 bg-white">
                           <tr v-for="item in logs" :key="item.id">
                              <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm text-gray-900 sm:pl-6">
                                 {{ formatDate(item.created_at) }}
                                 <span v-if="item.description && item.description.includes('Withdrawal')" class="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200 uppercase">Payout</span>
                              </td>
                              <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-900 font-semibold">
                                 <span v-if="item.booking_number">{{ item.booking_number }}</span>
                                 <span v-else class="text-gray-400 font-normal">-</span>
                              </td>
                              <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500" v-if="isAgencyAdmin">
                                 {{ item.agent_name }}
                                 <span v-if="item.agent_id === 0" class="ml-1.5 px-1 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-medium border border-gray-200">System</span>
                              </td>
                              <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                 {{ item.amount_usd ? `$${Number(item.amount_usd).toFixed(2)}` : '-' }}
                              </td>
                              <td class="whitespace-nowrap px-3 py-4 text-sm text-right pr-6 font-bold" :class="Number(item.points) >= 0 ? 'text-emerald-600' : 'text-rose-600'">
                                 {{ Number(item.points) >= 0 ? `+${Number(item.points).toFixed(2)}` : Number(item.points).toFixed(2) }}
                              </td>
                           </tr>
                           <tr v-if="logs && logs.length === 0">
                              <td :colspan="isAgencyAdmin ? 5 : 4" class="text-center whitespace-nowrap py-8 pl-4 pr-3 text-sm font-medium text-gray-500 sm:pl-6">
                                 No loyalty point transactions found.
                              </td>
                           </tr>
                        </tbody>
                     </table>
                      
                      <!-- Pagination Controls -->
                      <div v-if="totalPages > 1" class="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                         <div class="flex flex-1 justify-between sm:hidden">
                            <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1" class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Previous</button>
                            <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages" class="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Next</button>
                         </div>
                         <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                            <div>
                               <p class="text-xs font-semibold text-gray-500">
                                  Showing
                                  <span class="font-bold text-gray-900">{{ (currentPage - 1) * limit + 1 }}</span>
                                  to
                                  <span class="font-bold text-gray-900">{{ Math.min(currentPage * limit, totalRecords) }}</span>
                                  of
                                  <span class="font-bold text-gray-900">{{ totalRecords }}</span>
                                  results
                               </p>
                            </div>
                            <div>
                               <nav class="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                                  <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1" class="relative inline-flex items-center rounded-l-md px-2 py-1.5 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50">
                                     <span class="sr-only">Previous</span>
                                     <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fill-rule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clip-rule="evenodd" />
                                     </svg>
                                  </button>
                                  <button v-for="page in visiblePages" :key="page" @click="changePage(page)" :class="page === currentPage ? 'relative z-10 inline-flex items-center bg-emerald-600 px-3 py-1 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-600' : 'relative inline-flex items-center px-3 py-1 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'">
                                     {{ page }}
                                  </button>
                                  <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages" class="relative inline-flex items-center rounded-r-md px-2 py-1.5 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50">
                                     <span class="sr-only">Next</span>
                                     <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                        <path fill-rule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clip-rule="evenodd" />
                                     </svg>
                                  </button>
                               </nav>
                            </div>
                         </div>
                      </div>
                   </div>
                </div>
             </div>
          </div>
       </main>
    </div>

    <!-- Bank account details Modal for points redemption -->
    <Modal :show="openModal" :canClose="true" @closed="handleModalClose">
       <template v-slot:content>
          <div class="w-full max-w-md mx-auto bg-white rounded-3xl p-6 md:p-8 shadow-[0_12px_40px_rgba(0,0,0,0.12)] border border-slate-100 flex flex-col relative">
             <!-- Modal Header -->
             <div class="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                <div class="text-left">
                   <h2 class="text-lg font-bold text-gray-900 tracking-tight">Request Loyalty Points Payout</h2>
                   <p class="text-xs text-gray-500 font-medium mt-1">Review bank details and confirm cashout points.</p>
                </div>
                <button @click="handleModalClose" class="text-gray-400 hover:text-gray-600 transition-colors p-1.5 hover:bg-gray-50 rounded-lg">
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-5 h-5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                   </svg>
                </button>
             </div>

             <!-- Case 1: Bank details are missing in profile -->
             <div v-if="!agentProfileDetails || !agentProfileDetails.bank_name || !agentProfileDetails.bank_account_name || !agentProfileDetails.bank_account_number || !agentProfileDetails.country" 
                  class="flex flex-col items-center justify-center py-6 text-center">
                <div class="h-16 w-16 bg-rose-50 rounded-full flex items-center justify-center text-rose-500 mb-4 ring-8 ring-rose-50/50">
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-8 h-8">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                   </svg>
                </div>
                <h3 class="text-base font-bold text-gray-900">Missing Bank Details</h3>
                <p class="mt-2 text-sm text-gray-500 leading-relaxed max-w-xs font-regular text-center">
                   Kindly fill the missing bank details in your profile first:
                   <span class="block mt-1 font-bold text-rose-600 tracking-wide">{{ missingBankFields.join(', ') }}</span>
                </p>
                <router-link to="/agent/settings" class="mt-6 px-5 py-3 bg-theme-active hover:bg-theme-active/95 text-white font-extrabold text-xs uppercase tracking-wider rounded-xl transition-all shadow-md active:scale-95 text-center">
                   Go to Profile Settings
                </router-link>
             </div>

             <!-- Case 2: Bank details exist, show values and withdraw points count input -->
             <div v-else class="flex flex-col gap-6">
                <!-- Bank Info Details Summary Preview -->
                <div class="bg-gray-50 rounded-2xl p-5 border border-gray-200/60 flex flex-col gap-3 text-left">
                   <div class="flex justify-between border-b border-gray-200/40 pb-2">
                      <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Bank Name</span>
                      <span class="text-sm font-bold text-gray-800">{{ agentProfileDetails.bank_name }}</span>
                   </div>
                   <div class="flex justify-between border-b border-gray-200/40 pb-2">
                      <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Account Title</span>
                      <span class="text-sm font-bold text-gray-800">{{ agentProfileDetails.bank_account_name }}</span>
                   </div>
                   <div class="flex justify-between border-b border-gray-200/40 pb-2">
                      <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Account / IBAN</span>
                      <span class="text-sm font-bold text-gray-800">{{ agentProfileDetails.bank_account_number }}</span>
                   </div>
                   <div class="flex justify-between pb-1">
                      <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Bank Country</span>
                      <span class="text-sm font-bold text-gray-800">{{ agentProfileDetails.country || '-' }}</span>
                   </div>
                </div>

                <!-- Input points box -->
                <div class="flex flex-col gap-2 text-left">
                   <label for="withdraw-points" class="uppercase text-xs font-extrabold text-gray-400 tracking-wider">Points to Withdraw *</label>
                   <div class="relative rounded-xl border border-gray-200 flex items-center bg-gray-50 focus-within:bg-white focus-within:border-theme-active transition-all duration-200">
                      <input v-model="pointsToWithdraw" 
                             type="number" 
                             id="withdraw-points" 
                             :max="loyaltySummary.points_earned"
                             placeholder="Enter points count" 
                             class="w-full bg-transparent outline-none p-3.5 font-bold text-gray-800 text-base" />
                      <span class="pr-4 text-xs font-bold text-emerald-600 whitespace-nowrap">
                         ${{ (Number(pointsToWithdraw || 0) * (Number(loyaltySummary.redeem_rate) || 0.1)).toFixed(2) }} USD
                      </span>
                   </div>
                   <span v-if="pointsError" class="text-xs text-rose-600 font-semibold mt-1">{{ pointsError }}</span>
                   <p class="text-[11px] text-gray-400 mt-1 font-medium">Available loyalty points balance: <strong class="text-gray-700 font-bold">{{ loyaltySummary.points_earned }}</strong></p>
                </div>

                <!-- Action Buttons -->
                <div class="flex items-center justify-end gap-3 mt-4">
                   <button type="button" @click="handleModalClose" class="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl text-xs font-bold text-gray-600 uppercase transition-all">Cancel</button>
                   <button type="button" @click="handleModalSubmit" :disabled="isBusy" class="px-5 py-2.5 bg-theme-active hover:bg-theme-active/95 text-white rounded-xl text-xs font-bold uppercase transition-all flex items-center gap-1.5 shadow-md disabled:opacity-50">
                      <svg v-if="isBusy" class="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                         <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                         <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Confirm Payout
                   </button>
                </div>
                <p v-if="loyaltyRedeemForm.error" class="text-ef-error mt-2 text-left text-xs font-semibold">{{ loyaltyRedeemForm.error }}</p>
             </div>
          </div>
       </template>
    </Modal>

   <Notification :show="notif.show" :type="notif.type" :message="notif.message" :messageDetails="notif.messageDetails" @closed="notifClosed"/>
   <Loader :show="isLoading" />
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { isLoggedIn, handleApiError, logout } from '@/utils/common'
import Agent from '@/composables/admin/agent'
import Modal from '@/widgets/Modal.vue'
import Loader from '@/widgets/Loader.vue'
import Notification from '@/widgets/Notification.vue'
import { NotificationType } from '@/types/index'
import router from '@/router'

const auth = isLoggedIn()
const isAgencyAdmin = computed(() => auth && auth.user && auth.user.type === 'agent' && auth.user.sub_type === null)

const isLoading = ref<boolean>(true)
const isBusy = ref<boolean>(false)
const openModal = ref<boolean>(false)
const { loyaltyLogs, requestRedeemLoyalty, agentProfile } = Agent()

const logs = ref<any[]>([])
const loyaltySummary = ref<any>(null)
const currentPage = ref<number>(1)
const totalPages = ref<number>(1)
const totalRecords = ref<number>(0)
const limit = ref<number>(10)

const notif = ref<NotificationType>({ show: false, type: 'success', message: '', messageDetails: '' })

const loyaltyRedeemForm = ref<any>({
   bankName: { value: '', error: false, errorMessage: '' },
   accountName: { value: '', error: false, errorMessage: '' },
   accountNumber: { value: '', error: false, errorMessage: '' },
   countryName: { value: '', error: false, errorMessage: '' },
   error: ''
})

const agentProfileDetails = ref<any>(null)
const pointsToWithdraw = ref<number | null>(null)
const pointsError = ref<string>('')
const isProfileLoading = ref<boolean>(false)

const missingBankFields = computed(() => {
   const missing = []
   if (!agentProfileDetails.value) return ['Bank Name', 'Account Title', 'Account Number/IBAN', 'Country']
   if (!agentProfileDetails.value.bank_name) missing.push('Bank Name')
   if (!agentProfileDetails.value.bank_account_name) missing.push('Account Title')
   if (!agentProfileDetails.value.bank_account_number) missing.push('Account Number or IBAN')
   if (!agentProfileDetails.value.country) missing.push('Country')
   return missing
})

onMounted(async () => {
   await fetchLoyaltyData()
})

const fetchLoyaltyData = async () => {
   isLoading.value = true
   const response = await loyaltyLogs(currentPage.value, limit.value)
   isLoading.value = false

   if (response.status === 200) {
      logs.value = response.data.logs || []
      loyaltySummary.value = response.data.loyalty_summary || null
      totalPages.value = response.data.last_page || 1
      totalRecords.value = response.data.total || 0
   } else if (response.response && response.response.status === 401) {
      logout(router)
   } else {
      const errorMsg = handleApiError(response)
      notif.value = { show: true, type: 'error', message: 'Failed to load', messageDetails: errorMsg }
      setTimeout(() => { notifClosed() }, 5000)
   }
}

const changePage = async (page: number) => {
   if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page
      await fetchLoyaltyData()
   }
}

const visiblePages = computed(() => {
   const pages = []
   const start = Math.max(1, currentPage.value - 2)
   const end = Math.min(totalPages.value, currentPage.value + 2)
   for (let i = start; i <= end; i++) {
      pages.push(i)
   }
   return pages
})

const handleRedeemPoints = async () => {
   isProfileLoading.value = true
   const profileRes = await agentProfile()
   isProfileLoading.value = false
   if (profileRes.status === 200) {
      agentProfileDetails.value = profileRes.data.data
      
      loyaltyRedeemForm.value.bankName.value = profileRes.data.data.bank_name || ''
      loyaltyRedeemForm.value.accountName.value = profileRes.data.data.bank_account_name || ''
      loyaltyRedeemForm.value.accountNumber.value = profileRes.data.data.bank_account_number || ''
      loyaltyRedeemForm.value.countryName.value = profileRes.data.data.country || ''
      
      pointsToWithdraw.value = loyaltySummary.value ? Math.floor(Number(loyaltySummary.value.points_earned)) : 0
   }
   openModal.value = true
}

const handleModalClose = () => {
   openModal.value = false
}

const notifClosed = () => {
   notif.value.show = false
}

const validateForm = (): boolean => {
   pointsError.value = ''
   let response = true
   
   if (!pointsToWithdraw.value || pointsToWithdraw.value <= 0) {
      pointsError.value = 'Please enter a valid points amount'
      response = false
   } else if (loyaltySummary.value && pointsToWithdraw.value > Number(loyaltySummary.value.points_earned)) {
      pointsError.value = 'Cannot exceed your accumulated points'
      response = false
   }

   return response
}

const handleModalSubmit = async () => {
   const isValid = validateForm()

   if (isValid && !isBusy.value && auth && auth.user) {
      isBusy.value = true
      const response = await requestRedeemLoyalty(loyaltyRedeemForm.value, pointsToWithdraw.value || 0)
      isBusy.value = false

      if (response.status === 200) {
         openModal.value = false
         notif.value = { show: true, type: 'success', message: 'Success', messageDetails: 'Redemption request submitted successfully' }
         setTimeout(() => { notifClosed() }, 4000)
         await fetchLoyaltyData()
      } else {
         const errorMsg = handleApiError(response)
         loyaltyRedeemForm.value.error = errorMsg
      }
   }
}

const formatDate = (dateStr: string) => {
   if (!dateStr) return '-'
   try {
      const [datePart] = dateStr.split(' ')
      const [year, month, day] = datePart.split('-')
      return `${day}-${month}-${year}`
   } catch (e) {
      return dateStr
   }
}
</script>
